import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { IoPlay } from "react-icons/io5";
import TermsAcceptanceModal from "../components/TermsAcceptanceModal";
import { hasAcceptedTerms, markTermsAccepted } from "../constants/termsAndConditions";
import { loginAsGuest } from "../services/authService";
import { useAuth } from "../services/AuthContext";
import { getStoredLanguage } from "../services/language";
import "./GuestLaunch.css";

const PREAUTH_TERMS_SUBJECT = "preauth";

const GUEST_LAUNCHES = Object.freeze({
  jugar: {
    kind: "bot",
    loadingText: "Preparando tu partida contra el bot...",
  },
  puzzle: {
    kind: "puzzle",
    mode: "dice",
    order: 8,
    loadingText: "Abriendo el puzzle 8 de Azar...",
  },
  aprender: {
    kind: "puzzle",
    mode: "free",
    order: 22,
    tutorialLevel: 1,
    loadingText: "Abriendo el Tutorial XIMA - Nivel 1...",
  },
});

const buildPuzzleTarget = ({ mode, order, tutorialLevel = 0 }) => {
  const params = new URLSearchParams({
    quickView: "puzzles",
    puzzleMode: mode,
    openPuzzleMode: mode,
    openPuzzleOrder: String(order),
  });
  if (tutorialLevel === 1) params.set("tutorialLevel", "1");
  const requestKey = `landing-${mode}-${order}-${Date.now()}`;

  return {
    pathname: `/game?${params.toString()}`,
    state: {
      source: "publicLanding",
      openMatchmakingQuickView: "puzzles",
      openMatchmakingQuickViewRequestKey: requestKey,
      puzzleMode: mode,
      openPuzzleMode: mode,
      openPuzzleOrder: order,
      openPuzzleRequestKey: requestKey,
      ...(tutorialLevel === 1 ? { tutorialLevel: 1 } : {}),
    },
  };
};

const createEasyScienceBotMatch = async () => {
  const response = await axios.post("/tournaments", {
    tournamentName: "",
    mode: "botlocalfree",
    memberSize: 2,
    havingTime: 15,
    botDifficulty: "easy",
    duration: 1,
    startingTime: new Date().toISOString(),
    isQuickMatch: false,
    isIndependentMatch: false,
    hasMatchPassword: false,
    matchPassword: "",
    tournamentPassword: "",
    joinPassword: "",
    tournamentRewardValue: 0,
    tournamentWinnerPrizeValue: 0,
  });

  const created = response?.data || null;
  const tournamentId = String(created?._id || created?.id || "").trim();
  if (!tournamentId) {
    throw new Error("No se recibió el identificador de la partida.");
  }

  return tournamentId;
};

const GuestLaunch = () => {
  const { action = "" } = useParams();
  const navigate = useNavigate();
  const { login: authLogin, user, loading: authLoading } = useAuth();
  const launchConfig = useMemo(
    () => GUEST_LAUNCHES[String(action || "").trim().toLowerCase()] || null,
    [action]
  );
  const [termsOpen, setTermsOpen] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const launchStartedRef = useRef(false);
  const guestSessionReadyRef = useRef(Boolean(user));

  useEffect(() => {
    if (user) guestSessionReadyRef.current = true;
  }, [user]);

  const launch = useCallback(async () => {
    if (!launchConfig || launchStartedRef.current) return;

    launchStartedRef.current = true;
    setTermsOpen(false);
    setErrorMessage("");
    setLaunching(true);

    try {
      if (!guestSessionReadyRef.current) {
        const response = await loginAsGuest();
        const guestToken = String(response?.data?.token || "").trim();
        if (!guestToken) {
          throw new Error("No se pudo iniciar la sesión de invitado.");
        }
        authLogin(guestToken, { persist: true });
        guestSessionReadyRef.current = true;
      }

      if (launchConfig.kind === "bot") {
        const tournamentId = await createEasyScienceBotMatch();
        navigate(`/game/${tournamentId}?autoplay=1`, {
          replace: true,
          state: {
            source: "publicLanding",
            fromLobbySource: "bot",
            botGamePreferences: {
              mode: "botlocalfree",
              memberSize: 2,
              havingTime: 15,
              botDifficulty: "easy",
            },
          },
        });
        return;
      }

      const target = buildPuzzleTarget(launchConfig);
      navigate(target.pathname, {
        replace: true,
        state: target.state,
      });
    } catch (error) {
      console.error("Guest quick launch failed:", error);
      launchStartedRef.current = false;
      setLaunching(false);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "No se pudo abrir el juego. Inténtalo nuevamente."
      );
    }
  }, [authLogin, launchConfig, navigate]);

  useEffect(() => {
    if (authLoading) return;

    if (!launchConfig) {
      navigate("/", { replace: true });
      return;
    }

    if (user || hasAcceptedTerms(PREAUTH_TERMS_SUBJECT)) {
      launch();
      return;
    }

    setTermsOpen(true);
  }, [authLoading, launch, launchConfig, navigate, user]);

  const acceptTermsAndLaunch = useCallback(() => {
    markTermsAccepted(PREAUTH_TERMS_SUBJECT);
    launch();
  }, [launch]);

  const cancelTerms = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  if (!launchConfig) return null;

  return (
    <main className="xima-guest-launch">
      <div className="xima-guest-launch-card">
        <img src="/mejor-logo-336.png" alt="XIMA" />
        <span className="xima-guest-launch-spinner" aria-hidden="true">
          <IoPlay />
        </span>
        <h1>{launchConfig.loadingText}</h1>
        <p>Entrarás como invitado, sin registrarte.</p>

        {errorMessage ? (
          <div className="xima-guest-launch-error" role="alert">
            <strong>No se pudo continuar</strong>
            <span>{errorMessage}</span>
            <div>
              <button type="button" onClick={launch} disabled={launching}>
                Reintentar
              </button>
              <button type="button" className="is-secondary" onClick={() => navigate("/")}>
                Volver
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <TermsAcceptanceModal
        open={termsOpen}
        mode="guest"
        loading={launching}
        language={getStoredLanguage()}
        onAccept={acceptTermsAndLaunch}
        onCancel={cancelTerms}
      />
    </main>
  );
};

export default GuestLaunch;
